import { useMemo } from 'react';

import {
  AppBar,
  Avatar,
  Box,
  Button,
  ButtonBase,
  IconButton,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Tooltip,
} from '@material-ui/core';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import SearchIcon from '@material-ui/icons/Search';
import { useModal } from 'react-modal-hook';

import ImageViewer from 'components/ImageViewer';
import StyledChannelChipIcon from 'components/StyledChannelChipIcon';
import { useAppDispatch, useAppSelector } from 'store';
import { TipoCanalEnum } from 'store/reducers/canais';
import { gruposSelector, setGrupoSelecionado } from 'store/reducers/grupos';

import DrawerDetalhesGrupo from './DrawerDetalhesGrupo';
import SelectorCanalEnvio from './SelectorCanalEnvio';

const useStyles = makeStyles(({ palette, spacing, shape }) => ({
  root: {
    display: 'flex',
    overflow: 'hidden',
    marginBottom: spacing(1),
    borderRadius: shape.borderRadius,
    backgroundColor: palette.background.paper,
    color: palette.text.primary,
  },
}));

interface TopContentAppBarProps {
  onPesquisarMensagens: () => void;
}

export default function TopContentAppBar({
  onPesquisarMensagens,
}: TopContentAppBarProps) {
  const classes = useStyles();
  const { palette } = useTheme();
  const dispatch = useAppDispatch();
  const { grupoSelecionado, canalSelecionadoId } =
    useAppSelector(gruposSelector);

  const canaisDoGrupo = useMemo(
    () => grupoSelecionado?.grupoChatCanais.filter(g => g.isAtivo) ?? [],
    [grupoSelecionado],
  );

  const selectedCanal = useMemo(() => {
    return canaisDoGrupo.find(c => c.id === canalSelecionadoId);
  }, [canaisDoGrupo, canalSelecionadoId]);

  const handleBack = () => {
    dispatch(setGrupoSelecionado());
  };

  const [showDrawerGrupo, hideDrawerGrupo] = useModal(
    ({ in: open }) => {
      if (grupoSelecionado) {
        return (
          <DrawerDetalhesGrupo
            open={open}
            onClose={hideDrawerGrupo}
            grupoSelecionado={grupoSelecionado}
          />
        );
      }

      return null;
    },
    [grupoSelecionado],
  );

  return (
    <AppBar className={classes.root} position="relative" elevation={0}>
      <ListItem dense>
        <Box mr={1}>
          <ButtonBase onClick={handleBack} disableRipple>
            <ArrowBackIcon />
          </ButtonBase>
        </Box>

        <ListItemAvatar style={{ minWidth: 40, paddingRight: 8 }}>
          <ImageViewer src={grupoSelecionado?.foto}>
            <Avatar src={grupoSelecionado?.foto} />
          </ImageViewer>
        </ListItemAvatar>

        <ListItemText
          primary={grupoSelecionado?.nome}
          primaryTypographyProps={{ noWrap: true, style: { maxWidth: '40ch' } }}
          secondaryTypographyProps={{ component: 'div' }}
          secondary={
            <Box
              mt={0.25}
              display="flex"
              alignItems="center"
              style={{ gap: 6 }}
            >
              {canaisDoGrupo.map(canal => (
                <StyledChannelChipIcon
                  key={canal.id}
                  color={canal.isCanalOnline ? undefined : palette.error.main}
                  type={canal.tipoCanal as TipoCanalEnum}
                  label={canal.nome}
                />
              ))}
            </Box>
          }
        />

        {canalSelecionadoId && canaisDoGrupo?.length > 1 && (
          <Box mr={1}>
            <SelectorCanalEnvio
              canais={canaisDoGrupo}
              selectedCanal={selectedCanal}
            />
          </Box>
        )}

        <Button variant="outlined" onClick={showDrawerGrupo}>
          Dados do grupo
        </Button>

        <Tooltip title="Pesquisar mensagem">
          <IconButton onClick={onPesquisarMensagens}>
            <SearchIcon />
          </IconButton>
        </Tooltip>
      </ListItem>
    </AppBar>
  );
}
